import { memo } from "react";
import { Loader2, Building2 } from "lucide-react";
import { HotelListItem } from "@repo/types";
import { HotelTableItemRow } from "./HotelTableItemRow";

interface HotelTableProps {
  loading: boolean;
  hotels: HotelListItem[];
  onViewDetails: (hotel: HotelListItem) => void;
  onEdit: (hotel: HotelListItem) => void;
}

export const HotelTable = memo(
  ({ loading, hotels, onViewDetails, onEdit }: HotelTableProps) => {
    if (loading) {
      return (
        <div className="bg-white dark:bg-[#111c44] rounded-2xl p-16 shadow-sm border border-slate-200 dark:border-white/10 flex flex-col items-center justify-center gap-4">
          <Loader2
            className="animate-spin text-blue-600 dark:text-blue-400"
            size={32}
          />
          <p className="text-slate-500 dark:text-slate-400 font-medium text-sm">
            Accessing Hotel Registry...
          </p>
        </div>
      );
    }

    if (!hotels || hotels.length === 0) {
      return (
        <div className="bg-white dark:bg-[#111c44] rounded-2xl p-16 shadow-sm border border-slate-200 dark:border-white/10 flex flex-col items-center justify-center gap-4 text-center">
          <div className="w-16 h-16 rounded-2xl bg-slate-50 dark:bg-[#1b254b] flex items-center justify-center text-slate-400 dark:text-slate-500 border border-slate-100 dark:border-white/5">
            <Building2 size={32} />
          </div>
          <div className="space-y-1">
            <p className="text-lg font-bold text-slate-800 dark:text-slate-100">
              No data available
            </p>
            <p className="text-slate-500 dark:text-slate-400 text-sm font-medium max-w-sm">
              Try adjusting your filters or search query to find what you're
              looking for.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Desktop Table View */}
        <div className="hidden xl:block bg-white dark:bg-[#111c44] rounded-2xl shadow-sm border border-slate-200 dark:border-white/10 overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 dark:bg-[#1b254b]/50 border-b border-slate-200 dark:border-white/10">
                <th className="px-6 py-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Hotel Identity
                </th>
                <th className="px-6 py-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Contact & Digital
                </th>
                <th className="px-6 py-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  Status & Plan
                </th>
                <th className="px-6 py-4 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-center">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 dark:divide-white/5">
              {hotels.map((hotel) => (
                <HotelTableItemRow
                  key={hotel.HotelID}
                  hotel={hotel}
                  onViewDetails={onViewDetails}
                  onEdit={onEdit}
                  view="table"
                />
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile/Tablet Card View */}
        <div className="xl:hidden grid grid-cols-1 md:grid-cols-2 gap-4">
          {hotels.map((hotel) => (
            <HotelTableItemRow
              key={hotel.HotelID}
              hotel={hotel}
              onViewDetails={onViewDetails}
              onEdit={onEdit}
              view="card"
            />
          ))}
        </div>
      </div>
    );
  },
);
